import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, HeartPulse, Palette, FlaskConical, GraduationCap, Users,
  ArrowRight, Search, Compass, FileText, Home, Calendar, Star, DollarSign,
} from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import DemoBookingModal from "@/components/demos/DemoBookingModal";
import { DEMO_THEMES } from "@/data/demoThemes";

const GREEN = "#2E6B4F";
const DARK = "#1C3327";
const MIST = "#F1F6F2";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const U = "https://images.unsplash.com/";

const HERO = U + "photo-1763890498955-13f109b2fbd7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80&ixlib=rb-4.1.0";
const IMG_WALK = U + "photo-1663162551013-8bb8ab151e11?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&ixlib=rb-4.1.0";
const IMG_STUDY = U + "photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&ixlib=rb-4.1.0";
const IMG_GROUP = U + "photo-1758270705518-b61b40527e76?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&ixlib=rb-4.1.0";

const PROGRAMS = [
  { icon: Briefcase, t: "Business", d: "Management, finance, marketing & entrepreneurship." },
  { icon: HeartPulse, t: "Health Sciences", d: "Nursing, public health & pre-professional tracks." },
  { icon: Palette, t: "Arts & Humanities", d: "Writing, design, history, philosophy & languages." },
  { icon: FlaskConical, t: "Science & Technology", d: "Computer science, biology, data & engineering." },
  { icon: GraduationCap, t: "Education", d: "Teaching, curriculum & educational leadership." },
  { icon: Users, t: "Social Sciences", d: "Psychology, sociology, economics & policy." },
];
const JOURNEY = [
  { icon: Compass, t: "Explore", d: "Browse programs, tour campus, and talk with admissions." },
  { icon: FileText, t: "Apply", d: "Submit the free online application, transcripts & a short essay." },
  { icon: Home, t: "Belong", d: "Join clubs, move into housing, and find your people." },
];
const STATS = [
  { n: "12:1", l: "Student-to-faculty ratio" },
  { n: "92%", l: "Students receive aid" },
  { n: "80+", l: "Student clubs" },
  { n: "120", l: "Acre green campus" },
];
const EVENTS = [
  { d: "OCT 18", t: "Fall Open House", w: "Explore all six schools, meet faculty & tour campus." },
  { d: "NOV 02", t: "Financial Aid Night", w: "Free FAFSA help and a scholarships walkthrough." },
  { d: "NOV 15", t: "Early Action Deadline", w: "Priority review & merit scholarship consideration." },
];
const STORIES = [
  { img: IMG_STUDY, n: "Maya, '25", p: "Health Sciences", q: "The small classes meant my professors actually knew me — and pushed me." },
  { img: IMG_GROUP, n: "Andre, '24", p: "Science & Technology", q: "I built real projects with faculty from day one. That got me my first internship." },
];

export default function CedarGroveDemo() {
  const [book, setBook] = useState(false);
  const [prog, setProg] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Cedar Grove College (Demo) | WebsiteTech.pro"; }, []);
  const selects = [
    { key: "program", label: "Program of Interest", options: ["Undecided", ...PROGRAMS.map((p) => p.t)] },
    { key: "type", label: "Visit Type", options: ["Guided Campus Tour", "Virtual Tour", "Open House"] },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#fff", color: DARK, ...body }} data-testid="college-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: GREEN }}>WebsiteTech.pro Demo · Higher Education — view all demos</Link>
      <DemoTopNav testid="college-nav"
        logo={<div className="leading-tight flex items-center gap-2"><GraduationCap size={22} style={{ color: GREEN }} /><div><div className="text-lg tracking-[0.12em] font-bold" style={{ color: DARK }}>CEDAR GROVE</div><div className="text-[8px] tracking-[0.35em] uppercase" style={{ color: GREEN }}>College</div></div></div>}
        items={["Programs", "Admissions", "Campus Life", "Events", "Tuition & Aid"]} ctaLabel="Apply Now" onCta={() => setBook(true)}
        theme={{ bg: "rgba(255,255,255,0.94)", text: DARK, accent: GREEN, accentText: "#fff", border: "#dce8df" }} />

      {/* Hero */}
      <header data-testid="college-hero" className="relative">
        <img src={HERO} alt="Cedar Grove campus" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(28,51,39,0.86) 0%, rgba(28,51,39,0.55) 55%, rgba(28,51,39,0.2) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-36 text-white">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-200">Undergraduate · Graduate · Certificates</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Find your path.<br />Build what comes next.</h1>
            <p className="mt-5 text-lg text-white/85 max-w-lg">A welcoming college where small classes, real mentorship, and a green 120-acre campus help you become who you're meant to be.</p>
            {/* Program search */}
            <div data-testid="college-program-search" className="mt-8 bg-white rounded-xl p-2 flex items-center gap-2 max-w-md shadow-lg">
              <Search size={18} className="ml-2 shrink-0" style={{ color: GREEN }} />
              <select data-testid="college-search-select" onChange={(e) => setProg(e.target.value === "Undecided" ? null : e.target.value)} className="flex-1 bg-transparent text-sm outline-none py-2.5" style={{ color: DARK }}>
                <option>Undecided</option>
                {PROGRAMS.map((p) => <option key={p.t}>{p.t}</option>)}
              </select>
              <button onClick={() => setBook(true)} data-testid="college-search-btn" className="rounded-lg px-5 py-2.5 text-xs tracking-[0.12em] uppercase font-semibold text-white whitespace-nowrap" style={{ background: GREEN }}>Explore</button>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Programs */}
      <section data-testid="college-programs" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GREEN }}>Six Schools</span><h2 className="mt-2 text-3xl md:text-4xl" style={serif}>Study what moves you</h2></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROGRAMS.map((p, i) => (
            <button key={p.t} onClick={() => { setProg(p.t); setBook(true); }} data-testid={`college-program-${i}`}
              className="group text-left bg-white rounded-2xl p-6 border hover:shadow-lg transition-all hover:-translate-y-0.5" style={{ borderColor: prog === p.t ? GREEN : "#e3ede5" }}>
              <div className="h-12 w-12 rounded-xl grid place-items-center" style={{ background: `${GREEN}14`, color: GREEN }}><p.icon size={22} /></div>
              <h3 className="mt-4 text-xl font-semibold" style={serif}>{p.t}</h3>
              <p className="mt-1.5 text-sm opacity-70 leading-relaxed">{p.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: GREEN }}>Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
            </button>
          ))}
        </div>
      </section>

      {/* Stats band */}
      <section className="py-14" style={{ background: MIST }}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (<div key={s.l}><div className="text-4xl md:text-5xl font-bold" style={{ ...serif, color: GREEN }}>{s.n}</div><div className="mt-2 text-xs uppercase tracking-widest opacity-70">{s.l}</div></div>))}
        </div>
      </section>

      {/* Admissions journey */}
      <section data-testid="college-journey" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GREEN }}>Admissions</span><h2 className="mt-2 text-3xl md:text-4xl" style={serif}>Your path, in three steps</h2></div>
        <div className="grid md:grid-cols-3 gap-6">
          {JOURNEY.map((j, i) => (
            <div key={j.t} className="relative rounded-2xl p-8 border" style={{ borderColor: "#e3ede5", background: "#fff" }}>
              <span className="absolute -top-4 left-8 h-8 w-8 rounded-full grid place-items-center text-sm font-bold text-white" style={{ background: GREEN }}>{i + 1}</span>
              <j.icon size={26} style={{ color: GREEN }} />
              <h3 className="mt-4 text-xl font-semibold" style={serif}>{j.t}</h3>
              <p className="mt-1.5 text-sm opacity-70 leading-relaxed">{j.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <button onClick={() => setBook(true)} data-testid="college-apply-cta" className="rounded-full px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white inline-flex items-center gap-2" style={{ background: GREEN }}>Start your application — no fee <ArrowRight size={14} /></button>
        </div>
      </section>

      {/* Campus life */}
      <section data-testid="college-campus" className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-10 items-center">
        <img src={IMG_WALK} alt="Campus life" className="rounded-2xl h-[340px] w-full object-cover shadow-md" />
        <div>
          <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GREEN }}>Campus Life</span>
          <h2 className="mt-2 text-3xl md:text-4xl" style={serif}>Room to grow, together</h2>
          <p className="mt-4 opacity-75 leading-relaxed">From 80+ clubs and NCAA Division III athletics to residence halls and quiet study groves, life at Cedar Grove is about finding your people and your purpose.</p>
          <ul className="mt-6 space-y-2 text-sm">
            {["Residential campus with modern halls", "NCAA Division III athletics", "Maker spaces, studios & labs", "Health, wellness & career support"].map((x) => (
              <li key={x} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} /> {x}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Events */}
      <section data-testid="college-events" className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GREEN }}>Upcoming Events</span><h2 className="mt-2 text-3xl" style={serif}>Come see for yourself</h2></div>
          <button onClick={() => setBook(true)} className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: GREEN }}>Schedule a visit <ArrowRight size={14} /></button>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {EVENTS.map((e) => (
            <div key={e.t} className="rounded-2xl p-6 border flex gap-4" style={{ borderColor: "#e3ede5" }}>
              <div className="shrink-0 h-14 w-14 rounded-xl grid place-items-center text-center leading-tight text-white text-[11px] font-bold" style={{ background: GREEN }}><Calendar size={16} className="mx-auto mb-0.5" />{e.d}</div>
              <div><h3 className="font-semibold">{e.t}</h3><p className="mt-1 text-sm opacity-70">{e.w}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Student stories */}
      <section data-testid="college-stories" className="py-16" style={{ background: DARK, color: "#fff" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-300">Student Stories</span><h2 className="mt-2 text-3xl" style={serif}>Voices from the grove</h2></div>
          <div className="grid md:grid-cols-2 gap-6">
            {STORIES.map((s) => (
              <div key={s.n} className="rounded-2xl p-6 flex gap-5 items-start" style={{ background: "rgba(255,255,255,0.06)" }}>
                <img src={s.img} alt={s.n} className="h-20 w-20 rounded-xl object-cover shrink-0" />
                <div>
                  <div className="flex gap-1 mb-2 text-emerald-300">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</div>
                  <p className="text-lg leading-relaxed" style={serif}>"{s.q}"</p>
                  <div className="mt-3 text-sm text-white/70">{s.n} · {s.p}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affording */}
      <section data-testid="college-aid" className="max-w-5xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl items-center justify-center mb-4" style={{ background: `${GREEN}14`, color: GREEN }}><DollarSign size={26} /></div>
        <h2 className="text-3xl md:text-4xl" style={serif}>Affording Cedar Grove</h2>
        <p className="mt-4 opacity-75 max-w-2xl mx-auto leading-relaxed">92% of our students receive financial aid. Merit scholarships range from $5,000 to $20,000 per year, and need-based grants and work-study help make a degree possible. We'll even help you file the FAFSA — for free.</p>
        <button onClick={() => setBook(true)} className="mt-6 rounded-full px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white inline-flex items-center gap-2" style={{ background: GREEN }}>Talk to financial aid <ArrowRight size={14} /></button>
      </section>

      <footer data-testid="college-footer" className="text-white" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><GraduationCap size={20} className="text-emerald-300" /><span className="tracking-[0.14em] font-bold">CEDAR GROVE COLLEGE</span></div>
          <p className="opacity-70">Find your path. Build what comes next.</p>
          <p className="opacity-70">Admissions · Mon–Sat · 9:00 AM – 5:00 PM</p>
        </div>
      </footer>

      <AIConciergeWidget demo="college" concierge={DEMO_THEMES.college.concierge} />
      {book && <DemoBookingModal demo="college" title="Schedule a campus visit" accent={GREEN} submitLabel="Request a visit" selects={selects} prefill={prog ? { program: prog } : {}} onClose={() => { setBook(false); setProg(null); }} />}
    </div>
  );
}
